import * as XLSX from 'xlsx';
import { EXCEL_HEADER_KEYWORDS, SYMBOL_TO_CODE } from './constants';

/* ───────────────── Helpers ───────────────── */

function toStr(v) {
  if (v == null) return '';
  if (v instanceof Date) return v.toISOString();
  return String(v).trim();
}

function isBlank(v) {
  return v == null || (typeof v === 'string' && v.trim() === '');
}

function rowIsBlank(row) {
  return !row || row.every(isBlank);
}

function isNumeric(v) {
  if (typeof v === 'number') return true;
  if (v instanceof Date) return false;
  if (typeof v !== 'string') return false;
  return /^-?\(?\d[\d,\s.]*\)?$/.test(v.trim());
}

// Does a merge region at row r cover c and span > 1 column or > 1 row?
function isBigMerge(merges, r, c) {
  return merges.some(m =>
    r >= m.s.r && r <= m.e.r && c >= m.s.c && c <= m.e.c &&
    (m.e.c - m.s.c > 1 || m.e.r - m.s.r > 0)
  );
}

// Does a row have any merge spanning 2+ cells? Data rows never have merges;
// totals / title rows always do. That's a reliable boundary signal.
function rowHasWideMerge(merges, r /* , numCols */) {
  return merges.some(m =>
    m.s.r <= r && m.e.r >= r && (m.e.c - m.s.c) >= 1
  );
}

const KNOWN_CURRENCIES = new Set(['USD','EUR','GBP','ILS','JPY','CHF','CAD','AUD','NZD','SEK','NOK','DKK','HKD','SGD']);

export function normalizeCurrency(raw) {
  if (raw == null) return null;
  const s = String(raw).trim();
  if (!s) return null;
  if (SYMBOL_TO_CODE[s]) return SYMBOL_TO_CODE[s];
  const upper = s.toUpperCase();
  if (upper === 'NIS' || upper === 'SHEKEL' || upper === 'SHEKELS') return 'ILS';
  if (KNOWN_CURRENCIES.has(upper)) return upper;
  // Check each char for a symbol
  for (const ch of s) if (SYMBOL_TO_CODE[ch]) return SYMBOL_TO_CODE[ch];
  return null;
}

export function parseAmount(v) {
  if (v == null || v === '') return null;
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v !== 'string') return null;
  let s = v.trim();
  if (!s) return null;
  // Parentheses = negative: "(12.50)" → "-12.50"
  let negParen = false;
  if (/^\(.*\)$/.test(s)) { negParen = true; s = s.slice(1, -1); }
  // Strip currency symbols and whitespace
  s = s.replace(/[₪$€£¥\s]/g, '');
  // Handle 1,234.56 vs 1.234,56 — if both separators, assume . is thousands if comma appears later
  if (s.indexOf(',') >= 0 && s.indexOf('.') >= 0) {
    if (s.lastIndexOf(',') > s.lastIndexOf('.')) {
      s = s.replace(/\./g, '').replace(',', '.');
    } else {
      s = s.replace(/,/g, '');
    }
  } else if (s.indexOf(',') >= 0) {
    // Only commas — treat as thousands if pattern looks like 1,234 else decimal
    if (/^-?\d{1,3}(,\d{3})+$/.test(s)) s = s.replace(/,/g, '');
    else s = s.replace(',', '.');
  }
  const n = parseFloat(s);
  if (!Number.isFinite(n)) return null;
  return negParen ? -n : n;
}

export function parseDateValue(v) {
  if (v == null || v === '') return null;
  if (v instanceof Date) {
    if (isNaN(v.getTime())) return null;
    return `${v.getFullYear()}-${String(v.getMonth()+1).padStart(2,'0')}-${String(v.getDate()).padStart(2,'0')}`;
  }
  // Excel serial number
  if (typeof v === 'number' && v > 20000 && v < 80000) {
    const d = XLSX.SSF.parse_date_code(v);
    if (!d) return null;
    return `${d.y}-${String(d.m).padStart(2,'0')}-${String(d.d).padStart(2,'0')}`;
  }
  if (typeof v !== 'string') return null;
  const s = v.trim();
  if (!s) return null;
  // DD.MM.YY / DD.MM.YYYY / DD/MM/YY / DD-MM-YYYY
  let m = s.match(/^(\d{1,2})[.\/\-](\d{1,2})[.\/\-](\d{2,4})$/);
  if (m) {
    const [, dd, mm, yy] = m;
    const year = yy.length === 2 ? 2000 + parseInt(yy, 10) : parseInt(yy, 10);
    const month = parseInt(mm, 10), day = parseInt(dd, 10);
    if (month < 1 || month > 12 || day < 1 || day > 31) return null;
    return `${year}-${String(month).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
  }
  // ISO YYYY-MM-DD
  m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (m) {
    const [, y, mm, dd] = m;
    const month = parseInt(mm, 10), day = parseInt(dd, 10);
    if (month < 1 || month > 12 || day < 1 || day > 31) return null;
    return `${y}-${String(month).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
  }
  // Fallback: Date.parse
  const d = new Date(s);
  if (!isNaN(d.getTime())) {
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  }
  return null;
}

/* ───────────────── Workbook parsing ───────────────── */

export function parseWorkbook(arrayBuffer) {
  const wb = XLSX.read(arrayBuffer, { type: 'array', cellDates: true });
  return wb.SheetNames.map(name => sheetFromWorkbook(wb, name));
}

// For testing: accept a workbook directly
export function sheetFromWorkbook(wb, name) {
  const ws = wb.Sheets[name];
  const aoa = XLSX.utils.sheet_to_json(ws, {
    header: 1, defval: null, raw: true, blankrows: true,
  });
  return { name, aoa, merges: ws['!merges'] || [] };
}

/* ───────────────── Detection ───────────────── */

// Score a row as a potential header row.
export function scoreHeaderRow(aoa, rowIdx, merges) {
  const row = aoa[rowIdx] || [];
  if (rowIsBlank(row)) return 0;
  let score = 0;
  let keywordMatches = 0;
  for (let c = 0; c < row.length; c++) {
    const s = toStr(row[c]);
    if (!s) continue;
    if (isBigMerge(merges, rowIdx, c)) return -10;  // merged title rows are never headers
    for (const re of Object.values(EXCEL_HEADER_KEYWORDS)) {
      if (re.test(s)) { score += 2; keywordMatches++; break; }
    }
  }
  if (keywordMatches === 0) return 0;
  // Look-ahead: does the row below look like data?
  const next = aoa[rowIdx + 1] || [];
  if (!rowIsBlank(next)) {
    const hasNum = next.some(v => typeof v === 'number' || v instanceof Date || (typeof v === 'string' && isNumeric(v)));
    const hasStr = next.some(v => typeof v === 'string' && v.trim() && !isNumeric(v));
    if (hasNum && hasStr) score += 3;
    if (hasNum) score += 1;
  }
  // Bonus: row is mostly short string cells (labels)
  const nonEmpty = row.filter(v => !isBlank(v));
  const allLabels = nonEmpty.length >= 2 && nonEmpty.every(v =>
    typeof v === 'string' && v.trim().length > 0 && v.trim().length <= 40 && !isNumeric(v)
  );
  if (allLabels) score += 1;
  return score;
}

function tableFromCandidate(candidate, warnings = []) {
  return {
    headerRow: candidate.row,
    startRow: candidate.startRow,
    endRow: candidate.endRow,
    warnings,
  };
}

function tableRangesOverlap(a, b) {
  const aStart = a.row;
  const aEnd = a.endRow;
  const bStart = b.row;
  const bEnd = b.endRow;
  return aStart <= bEnd && bStart <= aEnd;
}

function rankedTableCandidates(sheet) {
  const { aoa, merges = [] } = sheet;
  if (!aoa || aoa.length === 0) return [];

  // Score every candidate row and collect the top ones
  const scored = [];
  for (let r = 0; r < aoa.length; r++) {
    const s = scoreHeaderRow(aoa, r, merges);
    if (s >= 3) scored.push({ row: r, score: s });
  }
  if (scored.length === 0) return [];

  // Sort by score desc, then row asc
  scored.sort((a, b) => b.score - a.score || a.row - b.row);

  // Walk data range for each candidate; keep the one with the longest run.
  // Ties in score go to the larger table.
  const candidates = scored.map(c => {
    const range = walkDataRange(aoa, merges, c.row);
    return { ...c, ...range, rows: range ? range.endRow - range.startRow + 1 : 0 };
  }).filter(c => c.rows > 0);

  candidates.sort((a, b) =>
    (b.score - a.score) ||
    (b.rows - a.rows) ||
    (a.row - b.row)
  );

  return candidates;
}

function nonOverlappingTableCandidates(sheet) {
  const accepted = [];
  for (const candidate of rankedTableCandidates(sheet)) {
    if (!accepted.some(existing => tableRangesOverlap(existing, candidate))) {
      accepted.push(candidate);
    }
  }
  return accepted;
}

export function detectTables(sheet) {
  const candidates = nonOverlappingTableCandidates(sheet);
  const warnings = candidates.length > 1 ? ['multiple_tables_detected'] : [];
  return candidates
    .slice()
    .sort((a, b) => a.row - b.row)
    .map(candidate => tableFromCandidate(candidate, warnings));
}

export function detectTable(sheet) {
  const candidates = nonOverlappingTableCandidates(sheet);
  if (candidates.length === 0) return null;
  const warnings = candidates.length > 1 ? ['multiple_tables_detected'] : [];
  return tableFromCandidate(candidates[0], warnings);
}

function walkDataRange(aoa, merges, headerRow) {
  const headerCells = aoa[headerRow] || [];
  const numCols = headerCells.length;
  const startRow = headerRow + 1;
  let endRow = startRow - 1;
  let blankStreak = 0;

  for (let r = startRow; r < aoa.length; r++) {
    const row = aoa[r] || [];
    if (rowIsBlank(row)) {
      blankStreak++;
      if (blankStreak >= 2) break;
      continue;
    }
    // Stop if row has a wide merge (totals / footer)
    if (rowHasWideMerge(merges, r, numCols)) break;
    // Stop if row is all non-numeric strings while still has content (likely footer legal text)
    const nonEmpty = row.filter(v => !isBlank(v));
    const allStrings = nonEmpty.length > 0 && nonEmpty.every(v => typeof v === 'string' && !isNumeric(v));
    if (allStrings && nonEmpty.length <= 2 && r > startRow) break;
    blankStreak = 0;
    endRow = r;
  }

  if (endRow < startRow) return null;
  return { startRow, endRow };
}

/* ───────────────── Column mapping ───────────────── */

export const COLUMN_ROLES = [
  'ignore', 'date', 'description', 'amount', 'currency',
  'foreignAmount', 'foreignCurrency', 'debit', 'credit', 'notes',
];

export function mapColumns(headerCells, { mainCurrency = 'ILS' } = {}) {
  const roles = (headerCells || []).map(() => 'ignore');
  const amountCols = [];
  const currencyCols = [];

  (headerCells || []).forEach((h, i) => {
    const s = toStr(h);
    if (!s) return;
    // Check most-specific matchers first
    if (EXCEL_HEADER_KEYWORDS.date.test(s))             roles[i] = 'date';
    else if (EXCEL_HEADER_KEYWORDS.balance.test(s))     roles[i] = 'ignore';
    else if (EXCEL_HEADER_KEYWORDS.debit.test(s))       roles[i] = 'debit';
    else if (EXCEL_HEADER_KEYWORDS.credit.test(s))      roles[i] = 'credit';
    else if (EXCEL_HEADER_KEYWORDS.notes.test(s))       roles[i] = 'notes';
    else if (EXCEL_HEADER_KEYWORDS.currency.test(s))    { roles[i] = 'currency'; currencyCols.push(i); }
    else if (EXCEL_HEADER_KEYWORDS.charge.test(s))      { roles[i] = 'amount'; amountCols.push({ i, isCharge: true }); }
    else if (EXCEL_HEADER_KEYWORDS.amount.test(s))      { roles[i] = 'amount'; amountCols.push({ i, isCharge: false }); }
    else if (EXCEL_HEADER_KEYWORDS.description.test(s)) roles[i] = 'description';
    else if (EXCEL_HEADER_KEYWORDS.receiptId.test(s))   roles[i] = 'ignore';
  });

  // Two amount columns: pick the billed one as `amount`, the other becomes `foreignAmount`.
  // Heuristic: prefer the column flagged as 'charge/billed'; otherwise use the one whose
  // adjacent currency column contains the main currency.
  if (amountCols.length >= 2) {
    const charge = amountCols.find(a => a.isCharge);
    const other  = amountCols.find(a => !a.isCharge) || amountCols[amountCols.length - 1];
    const primary = charge || amountCols[amountCols.length - 1];
    const secondary = amountCols.find(a => a.i !== primary.i);
    if (secondary) {
      roles[secondary.i] = 'foreignAmount';
      // Pair a currency column to each amount column (closest one).
      const currencyForPrimary = closestCurrency(currencyCols, primary.i);
      const currencyForSecondary = closestCurrency(currencyCols, secondary.i, currencyForPrimary);
      if (currencyForSecondary != null) roles[currencyForSecondary] = 'foreignCurrency';
    }
  }

  return { roles };
}

function closestCurrency(currencyCols, amountCol, exclude) {
  let best = null, bestDist = Infinity;
  for (const c of currencyCols) {
    if (c === exclude) continue;
    const d = Math.abs(c - amountCol);
    // Prefer the currency column that comes AFTER the amount column on ties
    // (Hebrew exports pair amount → currency left-to-right)
    if (d < bestDist || (d === bestDist && c > amountCol && best != null && best < amountCol)) {
      bestDist = d; best = c;
    }
  }
  return best;
}

/* ───────────────── Row extraction ───────────────── */

export function extractRows(sheet, detection, mapping, { mainCurrency = 'ILS' } = {}) {
  if (!detection) return [];
  const { aoa } = sheet;
  const { startRow, endRow } = detection;
  const { roles } = mapping;

  const idx = {
    date:           roles.indexOf('date'),
    description:    roles.indexOf('description'),
    amount:         roles.indexOf('amount'),
    currency:       roles.indexOf('currency'),
    foreignAmount:  roles.indexOf('foreignAmount'),
    foreignCurrency: roles.indexOf('foreignCurrency'),
    debit:          roles.indexOf('debit'),
    credit:         roles.indexOf('credit'),
    notes:          roles.indexOf('notes'),
  };

  const out = [];
  for (let r = startRow; r <= endRow; r++) {
    const row = aoa[r] || [];
    if (rowIsBlank(row)) continue;
    const parsed = buildParsedRow(row, idx, mainCurrency);
    if (parsed) out.push(parsed);
  }
  return out;
}

function sameDetectionRange(a, b) {
  return Boolean(a && b &&
    a.headerRow === b.headerRow &&
    a.startRow === b.startRow &&
    a.endRow === b.endRow
  );
}

export function extractRowsFromTables(
  sheet,
  tables,
  currentTable,
  currentMapping,
  { mainCurrency = 'ILS' } = {}
) {
  if (!tables || tables.length === 0) return [];

  return tables
    .slice()
    .sort((a, b) => a.headerRow - b.headerRow)
    .flatMap(table => {
      const mapping = sameDetectionRange(table, currentTable)
        ? currentMapping
        : mapColumns(sheet.aoa[table.headerRow] || [], { mainCurrency });
      return extractRows(sheet, table, mapping, { mainCurrency });
    });
}

function buildParsedRow(row, idx, mainCurrency) {
  const date = idx.date >= 0 ? parseDateValue(row[idx.date]) : null;
  const description = idx.description >= 0 ? toStr(row[idx.description]) : '';
  const notes = idx.notes >= 0 ? toStr(row[idx.notes]) : '';

  // Amount: from amount column, or derived from debit/credit
  let rawAmount = null;
  let type = 'expense';
  if (idx.debit >= 0 || idx.credit >= 0) {
    const deb = idx.debit  >= 0 ? parseAmount(row[idx.debit])  : null;
    const cre = idx.credit >= 0 ? parseAmount(row[idx.credit]) : null;
    if (cre != null && cre !== 0) { rawAmount = cre; type = 'income'; }
    else if (deb != null && deb !== 0) { rawAmount = deb; type = 'expense'; }
  } else if (idx.amount >= 0) {
    rawAmount = parseAmount(row[idx.amount]);
  }

  if (rawAmount == null || rawAmount === 0) return null;

  // Negative = refund → flip to income for credit-card-style sheets
  if (rawAmount < 0 && idx.debit < 0 && idx.credit < 0) type = 'income';

  const amount = Math.abs(rawAmount);

  // Currency
  let currency = mainCurrency;
  if (idx.currency >= 0) {
    const c = normalizeCurrency(row[idx.currency]);
    if (c) currency = c;
  }

  // Foreign amount handling
  let foreignAmount = null;
  let foreignCurrency = null;
  if (idx.foreignAmount >= 0) {
    const fa = parseAmount(row[idx.foreignAmount]);
    if (fa != null && fa !== 0) {
      let fc = null;
      if (idx.foreignCurrency >= 0) fc = normalizeCurrency(row[idx.foreignCurrency]);
      if (fc && fc !== currency) {
        foreignAmount = Math.abs(fa);
        foreignCurrency = fc;
      }
    }
  }

  const parsedCurrency = foreignCurrency || currency;

  const result = {
    date: date || '',
    description,
    amount,
    type,
    currency: parsedCurrency,
    exchangeRate: '',
    categoryId: null,
    notes,
  };
  if (!date) result.error = 'bad_date';
  if (foreignAmount != null) {
    // For PasteImport-shaped rows, represent the foreign currency as the "amount" currency.
    // amount becomes the foreign-amount; exchangeRate = NIS / foreign.
    result.currency = foreignCurrency;
    result.amount = foreignAmount;
    result.exchangeRate = foreignAmount > 0 ? (Math.abs(rawAmount) / foreignAmount).toFixed(4) : '';
  }
  return result;
}
